import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import { Container } from 'cstar-react-primitives/lib/redux-form/layout/Container';
import Markdown from 'markdown-to-jsx';

import {
    getCustomerSupportPath,
    getPrivacyPolicyPath,
    getFAQPath,
    ABOUT_PATHS
} from '../routing/flow';
import NotFound from '../error/NotFound';
import {
    getBrandCreditCardStatementName,
    getBrandLegalName,
    getBrandName,
    getBrandUrl,
    getReplyToEmailDisplayName,
    getBrandSupportedCardTypes,
    getViewBalancePhone,
    getViewBalanceUrl,
    isAccessoriesEnabled,
    isPaypalEnabled
} from '../brand/brandSelectors';
import { getSupportedUYO } from '../uyo/uyoSelectors';
import { getHostname } from '../session/utils';
import { getSupportedPaymentTypes } from '../payment/paymentSelectors';
import { getStaticMediaURL } from '../app/bootstrap';
import { AnchorToLink } from '../primitive/AnchorToLink';
import { FEATURES, isFeatureEnabled } from '../utils/featureFlag';

import {
    getBalanceOrCustomerSupportUrl,
    getViewBalanceUrlOrPhone,
    getTermsAndConditions,
    getFAQ,
    getPrivacyPolicy
} from './legalDocsUtils';


export const AboutStep = ({
    intl,
    match,
    supportsDigital,
    supportsPhysical,
    isUyoSupported,
    isAccessoriesSupported,
    isLegacyLegalDocs,
    isPaypalSupported,
    pageContext
}) => {
    const { viewBalancePhone, viewBalanceUrl, customerSupportUrl } = pageContext;
    let pageText = null;

    
    const extendedPageContext = {
        ...pageContext,
        viewBalanceUrl: getBalanceOrCustomerSupportUrl(viewBalanceUrl, customerSupportUrl),
        viewBalanceUrlOrPhone: getViewBalanceUrlOrPhone(intl, viewBalancePhone, viewBalanceUrl)
    };

    
    switch (match.params.docType) {
        case ABOUT_PATHS.TERMS_AND_CONDITIONS:
            pageText = getTermsAndConditions(intl, extendedPageContext, {
                supportsDigital,
                supportsPhysical,
                isUyoSupported
            });
            break;
        case ABOUT_PATHS.FAQ:
            pageText = getFAQ(intl, extendedPageContext, {
                supportsDigital,
                supportsPhysical,
                isAccessoriesSupported,
                isPaypalSupported
            });
            break;
        case ABOUT_PATHS.PRIVACY_POLICY:
            pageText = getPrivacyPolicy(intl);
            break;
        default:
            break;
    }

    if (!pageText) return (<NotFound />);

    
    if (isLegacyLegalDocs) {
        return (<NotFound />);
    }

    return (
        <Container hasSlimPadding>
            <Markdown
                className="about-content"
                options={{
                    overrides: {
                        
                        a: {
                            component: AnchorToLink
                        }
                    }
                }}
            >
                {pageText}
            </Markdown>

        </Container>
    );
};

AboutStep.defaultProps = {
    isLegacyLegalDocs: false
};

AboutStep.propTypes = {
    
    intl: intlShape.isRequired,
    
    match: PropTypes.object.isRequired,
    
    supportsDigital: PropTypes.bool.isRequired,
    supportsPhysical: PropTypes.bool.isRequired,
    isUyoSupported: PropTypes.bool.isRequired,
    isAccessoriesSupported: PropTypes.bool.isRequired,
    isPaypalSupported: PropTypes.bool.isRequired,
    isLegacyLegalDocs: PropTypes.bool,
    pageContext: PropTypes.object.isRequired
};

export const mapStateToProps = (state) => {
    const supportedCardTypes = getBrandSupportedCardTypes(state);
    const supportsDigital = supportedCardTypes.get('supportsDigitalCards');
    const supportsPhysical = supportedCardTypes.get('supportsPlasticCards');

    const uyoSupportedFeatures = getSupportedUYO(state);
    const isAnyUyoEgcSupported = () => (
        uyoSupportedFeatures.get('supportsUYOEgcPhoto') || uyoSupportedFeatures.get('supportsUYOEgcVideo')
    );
    const isAnyUyoPhysicalSupported = () => uyoSupportedFeatures.get('supportsUYOPlasticPhoto');

    let isUyoSupported = false;
    if (supportsDigital && supportsPhysical) {
        isUyoSupported = isAnyUyoEgcSupported() || isAnyUyoPhysicalSupported();
    } else if (supportsDigital) {
        isUyoSupported = isAnyUyoEgcSupported();
    } else if (supportsPhysical) {
        isUyoSupported = isAnyUyoPhysicalSupported();
    }

    return {
        supportsDigital,
        supportsPhysical,
        isUyoSupported,
        isAccessoriesSupported: isAccessoriesEnabled(state),
        isLegacyLegalDocs: isFeatureEnabled(FEATURES.LEGACY_LEGAL_DOCS),
        isPaypalSupported: isPaypalEnabled(state),
        pageContext: {
            brandName: getBrandName(state),
            brandLegalName: getBrandLegalName(state),
            brandUrl: getBrandUrl(state),
            creditCardStatementName: getBrandCreditCardStatementName(state),
            customerSupportUrl: getCustomerSupportPath(),
            faqUrl: getFAQPath(),
            privacyPolicyUrl: getPrivacyPolicyPath(),
            replyToEmailDisplayName: getReplyToEmailDisplayName(state),
            siteDomain: getHostname(),
            staticMediaUrl: getStaticMediaURL(),
            supportedPaymentTypes: getSupportedPaymentTypes(state),
            viewBalancePhone: getViewBalancePhone(state),
            viewBalanceUrl: getViewBalanceUrl(state)
        }
    };
};


export default connect(mapStateToProps)(injectIntl(AboutStep));
